import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, apiClient } from '@/lib/api/client';
import { useAuthStore } from '@/lib/auth/store';
import { ensureArray } from '@/lib/utils/ensure-array';
import type {
  ApiKeyDetail,
  ApiKeyRow,
  ApiKeySummaryRow,
  CreateApiKeyRequest,
  GenerateReportRequest,
  HistoricalSummaryRequest,
  UpdateApiKeyRequest,
} from './types';

export const apiKeyKeys = {
  all: ['api-keys'] as const,
  list: (userId: string | number) => ['api-keys', 'list', userId] as const,
  detail: (id: string | number) => ['api-keys', 'detail', id] as const,
  summary: (userId: string | number) =>
    ['api-keys', 'summary', userId] as const,
};

function useInvalidateKeys() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: apiKeyKeys.all });
}

/* ---------------- List + CRUD ---------------- */

/** GET /api/obd/userApikey/{userId} */
export function useApiKeyList() {
  const userId = useAuthStore((s) => s.userId);
  return useQuery({
    queryKey: apiKeyKeys.list(userId ?? 'none'),
    queryFn: () => api.get<ApiKeyRow[]>(`/api/obd/userApikey/${userId}`),
    enabled: Boolean(userId),
    staleTime: 30_000,
    select: (data) => ensureArray<ApiKeyRow>(data),
  });
}

/** GET /api/obd/getApiKey/{id} */
export function useApiKeyDetail(id: string | number | null) {
  return useQuery({
    queryKey: apiKeyKeys.detail(id ?? 'none'),
    queryFn: () => api.get<ApiKeyDetail>(`/api/obd/getApiKey/${id}`),
    enabled: Boolean(id),
    staleTime: 30_000,
  });
}

export function useCreateApiKey() {
  const invalidate = useInvalidateKeys();
  return useMutation({
    mutationFn: (req: CreateApiKeyRequest) =>
      api.post<{ message?: string }>('/api/obd/add/userApiKey', req),
    onSuccess: () => invalidate(),
  });
}

export function useUpdateApiKey() {
  const invalidate = useInvalidateKeys();
  return useMutation({
    mutationFn: (req: UpdateApiKeyRequest) =>
      api.post<{ message?: string }>('/api/obd/apikey/edit', req),
    onSuccess: () => invalidate(),
  });
}

/**
 * Legacy used POST with method override `DELETE`. The new client uses axios
 * directly — DELETE with empty body works fine.
 */
export function useDeleteApiKey() {
  const invalidate = useInvalidateKeys();
  return useMutation({
    mutationFn: (id: number) =>
      apiClient
        .delete<{ message?: string }>(`/api/obd/apikey/${id}`)
        .then((r) => r.data),
    onSuccess: () => invalidate(),
  });
}

/* ---------------- Summary ---------------- */

/** GET /api/obd/apikey/summary/{userId} */
export function useApiKeySummary() {
  const userId = useAuthStore((s) => s.userId);
  return useQuery({
    queryKey: apiKeyKeys.summary(userId ?? 'none'),
    queryFn: () => api.get<ApiKeySummaryRow[]>(`/api/obd/apikey/summary/${userId}`),
    enabled: Boolean(userId),
    staleTime: 30_000,
    select: (data) => ensureArray<ApiKeySummaryRow>(data),
  });
}

/** POST /api/obd/apikey/historical/summary */
export function useApiKeyHistoricalSummary() {
  return useMutation({
    mutationFn: (req: HistoricalSummaryRequest) =>
      api
        .post<ApiKeySummaryRow[]>('/api/obd/apikey/historical/summary', req)
        .then((res) => ensureArray<ApiKeySummaryRow>(res)),
  });
}

/** POST /api/obd/api/report/generate */
export function useGenerateApiReport() {
  return useMutation({
    mutationFn: (req: GenerateReportRequest) =>
      api.post<{ message?: string }>('/api/obd/api/report/generate', req),
  });
}
